import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import SensorMap from '../components/SensorMap';
import SensorLocations from '../components/SensorLocations';
import SensorDetails from '../components/SensorDetails';
import DataTable from '../components/DataTable';
import SensorVisualization from '../components/SensorVisualization';
import Footer from '../components/Footer';
import { locations } from '../data/locations';
import { ArrowLeft } from 'lucide-react';
import { getLocationVSN } from '../utils/locationUtils';

const LocationDashboard = () => {
  const { locationId, sensorId } = useParams();
  const navigate = useNavigate();
  const location = locations[locationId];
  
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [showDetailedData, setShowDetailedData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [timeRange, setTimeRange] = useState('hour');
  const [sensorData, setSensorData] = useState([]);
  const [lastReadings, setLastReadings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [mostRecentReading, setMostRecentReading] = useState(null);

  // Fetch latest readings for all sensors in this location
  const fetchLatestReadingsForAllSensors = useCallback(async () => {
    if (!locationId || !location) return;
    
    const vsn = getLocationVSN(locationId);
    const now = new Date();
    const startTime = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour ago

    try {
      const response = await axios.post('/api/proxy', {
        start: startTime.toISOString(),
        end: now.toISOString(),
        filter: {
          plugin: "registry.sagecontinuum.org/flozano/lorawan-listener:*.*",
          vsn: vsn,
          name: "uncorrected_inner|uncorrected_outer|battery_voltage"
        }
      });

      if (typeof response.data === 'string') {
        const dataArray = response.data.split('\n')
          .filter(line => line.trim() !== '')
          .map(line => JSON.parse(line));

        const latestReadings = {};
        location.sensors.forEach(sensor => {
          const sensorData = dataArray.filter(item => {
            const sensorId = item.meta['Serial Number_tag'] || 
                           item.meta['serial_number_tag'] || 
                           item.meta['serial number_tag'];
            return sensorId === sensor.id;
          });
          
          if (sensorData.length > 0) {
            const latestReading = sensorData.reduce((latest, current) => {
              return new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest;
            });

            const latestReadingExists = latestReadings[sensor.id];
            const newTimestamp = new Date(latestReading.timestamp);
            const existingTimestamp = latestReadingExists ? new Date(latestReadingExists.timestamp) : null;

            // Only update if this is a newer reading
            if (!latestReadingExists || newTimestamp > existingTimestamp) {
              latestReadings[sensor.id] = {
                timestamp: latestReading.timestamp,
                Date: newTimestamp.toISOString().split('T')[0],
                Time: newTimestamp.toTimeString().split(' ')[0],
                [latestReading.name]: latestReading.value,
                ...latestReadingExists // preserve other fields if they exist
              };
            }
          }
        });

        setLastReadings(prev => ({
          ...prev,
          ...latestReadings
        }));
      }
    } catch (error) {
      console.error('Error fetching latest readings:', error);
      setErrorMessage('Failed to fetch latest readings');
    }
  }, [location, locationId]);

  // Set up polling for latest readings
  useEffect(() => {
    fetchLatestReadingsForAllSensors();
    const interval = setInterval(fetchLatestReadingsForAllSensors, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [fetchLatestReadingsForAllSensors]);

  // Fetch sensor data for selected sensor
  const fetchSensorData = useCallback(async () => {
    if (!selectedSensor || !locationId) return;

    const vsn = getLocationVSN(locationId);
    setIsLoading(true);
    setErrorMessage('');
    const now = new Date();
    let start = new Date(now);

    switch (timeRange) {
      case 'hour':
        start.setHours(now.getHours() - 1);
        break;
      case 'day':
        start.setDate(now.getDate() - 1);
        break;
      case '3days':
        start.setDate(now.getDate() - 3);
        break;
      default:
        start.setHours(now.getHours() - 1);
    }

    try {
      const response = await axios.post('/api/proxy', {
        start: start.toISOString(),
        end: now.toISOString(),
        filter: {
          plugin: "registry.sagecontinuum.org/flozano/lorawan-listener:*.*",
          vsn: vsn,
          name: "uncorrected_inner|uncorrected_outer|battery_voltage"
        }
      });

      if (typeof response.data === 'string') {
        const dataArray = response.data.split('\n')
          .filter(line => line.trim() !== '')
          .map(line => JSON.parse(line));

        const parsedData = dataArray
          .filter(item => {
            const sensorId = item.meta['Serial Number_tag'] || 
                           item.meta['serial_number_tag'] || 
                           item.meta['serial number_tag'];
            return sensorId === selectedSensor.id;
          })
          .reduce((acc, item) => {
            const timestamp = new Date(item.timestamp);
            const date = timestamp.toISOString().split('T')[0];
            const time = timestamp.toTimeString().split(' ')[0];
            
            const key = `${date} ${time}`;
            if (!acc[key]) {
              acc[key] = { 
                Date: date, 
                Time: time, 
                timestamp: timestamp
              };
            }
            
            acc[key][item.name] = item.value;
            return acc;
          }, {});

        const finalData = Object.values(parsedData).sort((a, b) => b.timestamp - a.timestamp);
        setSensorData(finalData);

        if (finalData.length > 0) {
          setMostRecentReading(finalData[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching sensor data:', error);
      setSensorData([]);
      setErrorMessage('Failed to fetch sensor data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSensor, timeRange, locationId]);

  useEffect(() => {
    fetchSensorData();
  }, [fetchSensorData]);

  // Set initial selected sensor based on URL
  useEffect(() => {
    if (sensorId && location) {
      const sensor = location.sensors.find(s => s.id === sensorId);
      if (sensor) {
        setSelectedSensor(sensor);
      }
    }
  }, [sensorId, location]);

  // Validate location exists
  useEffect(() => {
    if (!location) {
      navigate('/');
    }
  }, [location, navigate]);

  // Event handlers
  const handleSensorSelect = useCallback((sensor) => {
    setSelectedSensor(sensor);
    setShowDetailedData(false);
    setCurrentPage(1);
    navigate(`/${locationId}/${sensor.id}`);
  }, [locationId, navigate]);

  const handleToggleDetailedData = useCallback(() => {
    setShowDetailedData(prev => !prev);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value));
    setCurrentPage(1);
  }, []);

  const handleTimeRangeChange = useCallback((event) => {
    setTimeRange(event.target.value);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((column) => {
    setSortDirection(current => {
      if (sortColumn === column) {
        return current === 'asc' ? 'desc' : 'asc';
      }
      return 'desc';
    });
    setSortColumn(column);
  }, [sortColumn]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Sort data
  const sortedData = React.useMemo(() => {
    return [...sensorData].sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sensorData, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Export functionality
  const exportToCSV = useCallback(() => {
    if (!selectedSensor || sortedData.length === 0) return;

    const headers = ['Date', 'Time', 'Uncorrected Inner', 'Uncorrected Outer', 'Battery Voltage'];
    const csvContent = [
      headers.join(','),
      ...sortedData.map(row => 
        `${row.Date},${row.Time},${row.uncorrected_inner || ''},${row.uncorrected_outer || ''},${row.battery_voltage ? row.battery_voltage + ' V' : ''}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `sensor_data_${selectedSensor.id}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [selectedSensor, sortedData]);

  if (!location) {
    return null;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow max-w-6xl mx-auto p-6 bg-gray-50 rounded-lg shadow-lg">
        <Header />
        
        {/* Navigation */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center text-green-700 hover:text-green-900 transition-colors duration-200"
          >
            <ArrowLeft size={20} className="mr-1" />
            Back to All Locations
          </button>
          <h2 className="text-2xl font-semibold mt-2 text-green-800">
            {location.name}
          </h2>
        </div>

        {/* Main content */}
        <SensorMap 
          sensors={location.sensors} 
          onSensorClick={handleSensorSelect} 
          selectedSensor={selectedSensor?.id}
          lastReadings={lastReadings}
        />
        
        <SensorLocations 
          sensors={location.sensors} 
          onSensorSelect={handleSensorSelect} 
          selectedSensor={selectedSensor} 
          lastReadings={lastReadings}
        />
        
        {selectedSensor && (
          <SensorDetails 
            sensor={selectedSensor}
            mostRecentData={mostRecentReading || lastReadings[selectedSensor.id]}
            onToggleDetailedData={handleToggleDetailedData}
            showDetailedData={showDetailedData}
          />
        )}
        
        {selectedSensor && showDetailedData && (
          <>
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Loading sensor data...</p>
              </div>
            ) : errorMessage ? (
              <div className="text-center py-8">
                <p className="text-red-500">{errorMessage}</p>
              </div>
            ) : (
              <>
                <DataTable 
                  currentData={currentData}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  timeRange={timeRange}
                  onTimeRangeChange={handleTimeRangeChange}
                  onExportCSV={exportToCSV}
                  onSort={handleSort}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
                <SensorVisualization data={sortedData} />
              </>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default LocationDashboard;